// Drawn SVG icons (critique decision 4): each means one thing and takes the state colour from
// currentColor. No emoji anywhere in the UI. Decorative: the button carries the accessible name.
import type { ReactNode } from "react";

function Svg({ children, flip }: { children: ReactNode; flip?: boolean }) {
  return (
    <svg
      className={flip ? "icon icon-flip" : "icon"}
      viewBox="0 0 24 24"
      width="24"
      height="24"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

/** Microphone, live. */
export const MicIcon = () => (
  <Svg>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" />
  </Svg>
);

/** Microphone with a slash: muted (never a speaker glyph). */
export const MicOffIcon = () => (
  <Svg>
    <path d="M15 10.5V6a3 3 0 0 0-5.7-1.3M9 9v2a3 3 0 0 0 4.6 2.5" />
    <path d="M5.5 11a6.5 6.5 0 0 0 10.4 5.2M18.5 11a6.4 6.4 0 0 1-.6 2.7M12 17.5V21M4 4l16 16" />
  </Svg>
);

/** Speaker with waves: Listen on. */
export const SpeakerIcon = () => (
  <Svg>
    <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" />
    <path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11" />
  </Svg>
);

/** Speaker with a slash: Listen off. */
export const SpeakerOffIcon = () => (
  <Svg>
    <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" />
    <path className="off-mark" d="M16 9.5l5 5M21 9.5l-5 5" />
  </Svg>
);

/** UI-C29 (MAJLIS-88): Listen off while another seat speaks. The speaker KEEPS its slash (consultant's ruling,
 * 2026-09-23); two sound-wave arcs ripple behind it in the speaking seat's colour. The slash is drawn last, over a
 * knock-out stroke in the disc's own tint (`off-halo`), so the arcs never cross it. Colours and motion: app.css. */
export const SpeakerOffActivityIcon = () => (
  <Svg>
    <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" />
    <g className="arcs">
      <path className="arc1" d="M15.5 9a4 4 0 0 1 0 6" />
      <path className="arc2" d="M18 6.5a7.5 7.5 0 0 1 0 11" />
    </g>
    <path className="off-halo" d="M16 9.5l5 5M21 9.5l-5 5" />
    <path className="off-mark" d="M16 9.5l5 5M21 9.5l-5 5" />
  </Svg>
);

/** The chevron of a dropdown (D-33). Drawn, not a background image: it is placed with `inset-inline-end`,
 * so it mirrors for Arabic on its own, and app.css keeps no data or third-party URL. */
export const ChevronDownIcon = () => (
  <Svg>
    <path d="M6 9.5l6 6 6-6" />
  </Svg>
);

/** A person's waveform: replay the ORIGINAL recording (UI-C5). */
export const ReplayOriginalIcon = () => (
  <Svg>
    <circle cx="7" cy="7" r="3" />
    <path d="M2.5 18a4.5 4.5 0 0 1 9 0" />
    <path d="M14 10v4M17 7.5v9M20 10v4" />
  </Svg>
);

/** A speaker with a language tag: VOICE the translation (UI-C6). */
export function VoiceTranslationIcon({ tag }: { tag?: string }) {
  return (
    <span className="voice-icon">
      <Svg>
        <path d="M3 9.5h3L10 6v12l-4-3.5H3z" />
        <path d="M13.5 9.5a3.5 3.5 0 0 1 0 5" />
      </Svg>
      {tag && (
        <span className="lang-tag" aria-hidden="true">
          {tag}
        </span>
      )}
    </span>
  );
}

/** Leave: a door and an arrow out (mirrors in RTL). */
export const LeaveIcon = () => (
  <Svg flip>
    <path d="M10 4H5v16h5" />
    <path d="M14 8l4 4-4 4M18 12H9" />
  </Svg>
);

/** MAJLIS-163 (UI-C10): the hang-up handset of the Leave pill and of "End majlis for everyone" - the call apps' red
 * handset, drawn as the approved mock-up draws it (filled). Symmetric, so it does not mirror. */
export const HangUpIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false" fill="currentColor">
    <path d="M12 9c-1.6 0-3.15.25-4.6.7v3.1c0 .4-.23.74-.56.9-.98.5-1.87 1.12-2.68 1.85-.18.18-.43.29-.7.29-.28 0-.53-.11-.71-.29L.29 13.08C.11 12.9 0 12.65 0 12.37c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.66c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.46 2.47c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.81-.74-1.7-1.36-2.68-1.86-.33-.16-.56-.5-.56-.9V9.7C15.15 9.25 13.6 9 12 9z" />
  </svg>
);

export const CloseIcon = () => (
  <Svg>
    <path d="M6 6l12 12M18 6L6 18" />
  </Svg>
);

export const ArrowDownIcon = () => (
  <Svg>
    <path d="M12 5v14M6 13l6 6 6-6" />
  </Svg>
);

export const SeatsIcon = () => (
  <Svg>
    <circle cx="9" cy="8" r="3" />
    <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
    <path d="M16 5.5a3 3 0 0 1 0 5.5M17.5 14.5a5.5 5.5 0 0 1 3 4.5" />
  </Svg>
);

export const CheckIcon = () => (
  <Svg>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </Svg>
);

/** MAJLIS-67: back (the record, the list, a closed search) - points to the inline START, so it mirrors in RTL. */
export const BackIcon = () => (
  <Svg flip>
    <path d="M15 5l-7 7 7 7" />
  </Svg>
);

/** MAJLIS-67: a row that opens something further in - points to the inline END, mirrored in RTL (1b, 1c). */
export const ChevronEndIcon = () => (
  <Svg flip>
    <path d="M9 5l7 7-7 7" />
  </Svg>
);

/** MAJLIS-67 (UI-C13): search this record. */
export const SearchIcon = () => (
  <Svg>
    <circle cx="10.5" cy="10.5" r="6" />
    <path d="M15 15l5 5" />
  </Svg>
);

/** MAJLIS-67 (UI-C13): the previous match (up the record). */
export const ChevronUpIcon = () => (
  <Svg>
    <path d="M6 15l6-6 6 6" />
  </Svg>
);

/** MAJLIS-66 (UI-C15): a read link - two chain links (the mock-up's Share and link rows). */
export const LinkIcon = () => (
  <Svg>
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
    <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
  </Svg>
);

/** MAJLIS-66 (UI-C14): the record's options - three dots. */
export const MoreIcon = () => (
  <Svg>
    <circle cx="5.5" cy="12" r="1.2" fill="currentColor" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    <circle cx="18.5" cy="12" r="1.2" fill="currentColor" />
  </Svg>
);

/** MAJLIS-66 (UI-C14): delete the record. */
export const TrashIcon = () => (
  <Svg>
    <path d="M4 7h16M9.5 7V4.5h5V7M6.5 7l1 13h9l1-13M10 11v5M14 11v5" />
  </Svg>
);

/** MAJLIS-66 (UI-S3): read only - an eye (the read-link reader's strip). */
export const EyeIcon = () => (
  <Svg>
    <path d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="2.5" />
  </Svg>
);

/** MAJLIS-126 (UI-C35, the approved mock-up): a drawn KEY - a Listed with key room (the owner's word). Not the lock:
 * the lock already means "new people knock" (UI-C30). */
export const KeyIcon = () => (
  <Svg>
    <circle cx="8" cy="15" r="4" />
    <path d="M10.8 12.2L20 3M16 7l3 3M14 9l2 2" />
  </Svg>
);

/** MAJLIS-126 (UI-C36): the Room list group's title - a bulleted list. */
export const ListIcon = () => (
  <Svg>
    <path d="M9 6h11M9 12h11M9 18h11" />
    <circle cx="4.5" cy="6" r=".6" />
    <circle cx="4.5" cy="12" r=".6" />
    <circle cx="4.5" cy="18" r=".6" />
  </Svg>
);

/** MAJLIS-153 (UI-C40): the record's Summary - a page with three lines (summary-mockup.html (a)). */
export const SummaryIcon = () => (
  <Svg>
    <path d="M6.5 3.5h11v17h-11z" />
    <path d="M9.5 8h5M9.5 11.5h5M9.5 15h3" />
  </Svg>
);

/** MAJLIS-154 (UI-C41): "Save as file" - an arrow down onto a line (summary-mockup.html (b)). */
export const SaveIcon = () => (
  <Svg>
    <path d="M12 4v11M7.5 10.5L12 15l4.5-4.5" />
    <path d="M5 19.5h14" />
  </Svg>
);

/** MAJLIS-176 (UI-C37, the admin mock-up (b)): reload the list - a circular arrow. */
export const RefreshIcon = () => (
  <Svg>
    <path d="M20 12a8 8 0 1 1-2.3-5.7" />
    <path d="M20 4v5h-5" />
  </Svg>
);

/** MAJLIS-176 (UI-C37, the admin mock-up (b), (e)): phrases - a speech bubble (a room's phrase count, the empty list). */
export const PhrasesIcon = () => (
  <Svg>
    <path d="M4.5 5.5h15v10.5H9l-4.5 4z" />
  </Svg>
);

/** MAJLIS-176 (UI-C37, the admin mock-up (a)): the password shown - the eye struck through ("hide"). */
export const EyeOffIcon = () => (
  <Svg>
    <path d="M2.5 12s3.5-6.5 9.5-6.5c1.6 0 3 .4 4.2 1M21.5 12s-3.5 6.5-9.5 6.5c-1.6 0-3-.4-4.2-1" />
    <path d="M4 20L20 4" />
  </Svg>
);
